import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { BookingChannel } from "@tdelivr/api-client/generated/tenant/organization-hierarchy.js";

import { boundedNote } from "../board.ts";
import {
  ADD_CHANNEL_REFUSAL,
  BOARD_NOTE,
  CHANNELS_READ_SCOPE,
  channelRow,
  LISTING_NOTE,
  matchesState,
  PER_FETCH,
  statesPresent,
} from "../channels.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `channels` in `17 WP16 Tenant Admin.dc.html`: where customers can book.
 *
 * **The recorded blocker was right about both of its halves, and the second half is why this page
 * waited.** It read:
 *
 * > Nothing on the platform models a booking channel. TEN-05 holds organisation units, and a
 * > listing of those — which it also lacks — would be a list of different things, not this board
 * > with columns missing.
 *
 * R-39 served five listings whose rows are the rows their board draws and stopped here, because
 * publishing `GET /v1/tenants/{tenantId}/organization-units` would have made this page report as no
 * longer blocked while answering a page of organisations where the design draws app builds and
 * phone numbers. That restraint is what made the record the obvious next step rather than a
 * surprise. [ADR 0091](../../../docs/adr/0091-a-booking-channel-is-a-part-of-a-tenant-and-nothing-recorded-one.md)
 * adds it.
 *
 * What is drawn and what is not — `../channels.ts` argues each:
 *
 * | drawn                           | source                                                       |
 * | ------------------------------- | ------------------------------------------------------------ |
 * | the rows                        | TEN-05 `GET /v1/tenants/{tenantId}/booking-channels`         |
 * | `Channel` — `Rider app (iOS)`   | `type`, through the design's own label map                   |
 * | `Surface` — `+254 700 000 111`  | `handle`; the two app rows' **version** has no source        |
 * | `Last booking` — `2 min ago`    | **nothing** — no booking records the channel it arrived on   |
 * | `done · live`, `closed · paused`| `status`                                                      |
 * | `waiting · in store review`     | **not drawn** — an app store's queue, not this platform's    |
 * | the four tiles                  | **not drawn** — two are R-04, one is the same store absence  |
 * | `Add channel`                   | drawn and refused — and the refusal is a **scope**, not a route |
 *
 * **The `Add channel` refusal is unusual on this console and is worth reading.** Almost every
 * refused command here names a route that does not exist; this one names a route that does. ADR
 * 0091 publishes `POST /v1/tenants/{tenantId}/booking-channels` taking exactly the form's five
 * fields, and this board asks for the read scope only. Granting the write is one line in
 * `navigation.ts` and one grant on the realm client, and it is deliberately a separate decision
 * from drawing the board — a console gaining the ability to open a booking surface should have its
 * own record.
 */

const hierarchy = services.tenant__organization_hierarchy;

export interface BookingChannelsProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `CityCab › Brand and channels › Channels`, the design's trail. */
  readonly crumbs?: readonly string[];
}

export function BookingChannels({ transport, session, crumbs }: BookingChannelsProps): ReactNode {
  const [channels, setChannels] = useState<readonly BookingChannel[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setChannels(undefined);
    setFailure(undefined);

    if (!session.scopes.has(CHANNELS_READ_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [CHANNELS_READ_SCOPE] });
      return;
    }

    try {
      const page = await hierarchy
        .createOrganizationHierarchyClient(transport)
        .listBookingChannels(session.tenantId, String(PER_FETCH), undefined, undefined);
      setChannels(page.channels);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [CHANNELS_READ_SCOPE]));
    }
  }, [transport, session.scopes, session.tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows =
    channels === undefined
      ? undefined
      : channels.filter((channel) => matchesState(channel, status)).map(channelRow);

  const bounded = boundedNote(channels?.length, truncated, "channels", "in channel-type order");

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Channels"
      source="TEN-05"
      // The design's own sentence, word for word.
      description="Where customers can book: app, website, WhatsApp, phone and partner APIs."
      columns={["Channel", "Surface", "Last booking"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No booking channel is recorded for this tenant yet. A row appears here once one is added."
      primary={{ label: "Add channel", refusal: ADD_CHANNEL_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (channels === undefined ? [] : statesPresent(channels)).map((label) => ({
          value: label,
          label,
        })),
        value: status,
        onChange: setStatus,
      }}
      note={{ kind: "info", glyph: "info", text: BOARD_NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote={LISTING_NOTE}
      exportName="booking-channels"
      detailSubtitle="Channels · TEN-05"
      detailNote={BOARD_NOTE}
    />
  );
}
